import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Segment,
  Message,
  Transition,
  Loader,
} from 'semantic-ui-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { userActions } from '../../actions';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as Yup from 'yup';
import './EntryForm.css';

// eslint-disable-next-line
const regexEmail = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
const regexMobile = /^09[0-9]{9}$/i;

const EntryForm = props => {
  const [formShow, setFormShow] = useState(props.form);

  useEffect(() => {
    setFormShow(props.form);
  }, [props.form]);

  return (
    <div className="entryForm">
      <div className="efContainer">
        <Transition.Group animation={'fade up'} duration={500}>
          {formShow === 'login' && (
            <div>
              <div className="content">
                <Login gotoRegister={() => setFormShow('register')} />
              </div>
            </div>
          )}
          {formShow === 'register' && (
            <div>
              <div className="content">
                <Register gotoLogin={() => setFormShow('login')} />
              </div>
            </div>
          )}
        </Transition.Group>
      </div>
    </div>
  );
};

const Login = props => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const loggingIn = useSelector(state => state.authentication.loggingIn);
  const loggingOut = useSelector(state => state.authentication.loggingOut);
  const loggedIn = useSelector(state => state.authentication.loggedIn);
  const forgotPasswordState = useSelector(state => state.authentication.forgotPasswordState);
  const resetPasswordState = useSelector(state => state.authentication.resetPasswordState);
  const error = useSelector(state => state.authentication.error);
  const [messageContent, setMessageContent] = useState('');
  const [step, setStep] = useState(1);
  const mobileRef = useRef('');

  useEffect(() => {
    if (loggedIn && !loggingOut && user) {
      if (user.role === 'referral') {
        history.push('/agents');
      } else {
        history.push('/home');
      }
    }

    if (error) {
      switch (error.status) {
        case 401:
          setMessageContent(t('loginError401'));
          break;
        case 404:
          setMessageContent(t('loginError404'));
          break;
        case 423:
          setMessageContent(t('loginError423'));
          break;
        default:
          setMessageContent(t('errorGlobalMessage'));
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, loggingIn]);

  useEffect(() => {
    switch (forgotPasswordState) {
      case 'success':
        setMessageContent(t('forgotPasswordSMS'));
        setStep(3);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 404:
            setMessageContent(t('userNotFound'));
            break;
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgotPasswordState]);

  useEffect(() => {
    switch (resetPasswordState) {
      case 'success':
        setMessageContent(t('passwordChangedSuccess'));
        setStep(1);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPasswordState]);

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .required(t('yupUsernameReq'))
      .matches(new RegExp(regexEmail.source + '|' + regexMobile.source , 'i'), t('yupUsernameRegex')),
    password: Yup.string().required(t('yupPasswordReq')),
  });

  const MobileSchema = Yup.object().shape({
    mobile: Yup.string()
      .required(t('yupMobileReq'))
      .matches(new RegExp(regexMobile.source), t('yupMobileRegex')),
  });

  const ResetPassSchema = Yup.object().shape({
    code: Yup.number()
      .min(6, t('yupConfirmCodeValidate'))
      .required(t('yupConfirmCodeReq')),
    password: Yup.string()
      .min(6, t('yupPasswordValidate'))
      .required(t('yupPasswordReq')),
    confirmPassword: Yup.string()
      .required(t('yupConfirmPassReq'))
      .oneOf([Yup.ref('password'), null], t('yupConfirmPassValidate')),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: values => {
      if (values.username && values.password) {
        dispatch(userActions.login(values.username, values.password));
      }
    },
  });

  const mobileFormik = useFormik({
    initialValues: {
      mobile: '',
    },
    validationSchema: MobileSchema,
    onSubmit: values => {
      mobileRef.current = values.mobile;
      dispatch(userActions.forgotPassword(values));
    },
  });

  const resetPassFormik = useFormik({
    initialValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: ResetPassSchema,
    onSubmit: values => {
      dispatch(
        userActions.resetPassword({
          mobile: mobileRef.current,
          code: values.code,
          password: values.password,
        }),
      );
    },
  });

  return (
    <div>
      <Grid centered verticalAlign="middle">
        <Grid.Column>
          <Message
            className="enteryFormMessage"
            hidden={
              !(
                forgotPasswordState === 'success' ||
                resetPasswordState === 'success' ||
                error != null
              )
            }
            floating
            error={error != null}
            positive={forgotPasswordState === 'success' || resetPasswordState === 'success'}
            header={step === 1 && error != null && t('loginErrorHeader')}
            content={messageContent}
          />
          {step === 1 && (
            <>
              <Header as="h2" textAlign="center" content={t('loginHeader')} />
              <Form size="large" className="VForm">
                <Segment basic>
                  <Form.Input
                    error={formik.touched.username ? formik.errors.username : null}
                    disabled={loggingIn}
                    required
                    fluid
                    icon="user"
                    placeholder={t('username')}
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                  />
                  <Form.Input
                    error={formik.touched.password ? formik.errors.password : null}
                    disabled={loggingIn}
                    required
                    fluid
                    icon="lock"
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                  />

                  <Button
                    type="button"
                    fluid
                    basic
                    className="vButton colorPrimary"
                    size="large"
                    style={{ marginTop: 35, marginBottom: 5 }}
                    loading={loggingIn}
                    onClick={formik.handleSubmit}
                    content={t('login')}
                  />
                  {/* <Button basic color="blue" fluid size="large">
                <Icon name="google" color="red" /> ورود با حساب کاربری گوگل
              </Button> */}
                </Segment>
              </Form>
            </>
          )}
          {step === 2 && (
            <>
              <Header as="h2" textAlign="center" content={t('forgotPassword')} />
              <Form size="large" className="VForm">
                <Segment basic>
                  <Form.Input
                    error={mobileFormik.touched.mobile ? mobileFormik.errors.mobile : null}
                    disabled={forgotPasswordState === 'request'}
                    required
                    fluid
                    icon="mobile alternate"
                    placeholder={t('mobile')}
                    name="mobile"
                    value={mobileFormik.values.mobile}
                    onChange={mobileFormik.handleChange}
                  />

                  <Button
                    fluid
                    basic
                    className="vButton colorPrimary"
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={forgotPasswordState === 'request'}
                    onClick={mobileFormik.handleSubmit}
                    content={t('confirm')}
                  />
                </Segment>
              </Form>
            </>
          )}
          {step === 3 && (
            <>
              <Header as="h2" textAlign="center" content={t('changePassword')} />
              <Form size="large" className="VForm">
                <Segment basic>
                  <Form.Input
                    error={resetPassFormik.touched.code ? resetPassFormik.errors.code : null}
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid
                    icon="tag"
                    placeholder={t('code')}
                    type="text"
                    name="code"
                    value={resetPassFormik.values.code}
                    onChange={resetPassFormik.handleChange}
                  />
                  <Form.Input
                    error={
                      resetPassFormik.touched.password ? resetPassFormik.errors.password : null
                    }
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid
                    icon="lock"
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={resetPassFormik.values.password}
                    onChange={resetPassFormik.handleChange}
                  />

                  <Form.Input
                    error={
                      resetPassFormik.touched.confirmPassword
                        ? resetPassFormik.errors.confirmPassword
                        : null
                    }
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid
                    icon="lock"
                    placeholder={t('confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    value={resetPassFormik.values.confirmPassword}
                    onChange={resetPassFormik.handleChange}
                  />

                  <Button
                    basic
                    className=" vButton colorPrimary"
                    fluid
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={resetPasswordState === 'request'}
                    onClick={resetPassFormik.handleSubmit}
                    content={t('confirm')}
                  />
                </Segment>
              </Form>
            </>
          )}
        </Grid.Column>
      </Grid>
      {step === 1 && (
        <>
          <div className="textBox">
            <span className="link" onClick={() => setStep(2)}>
              {t('forgotPassword')}
            </span>
          </div>
          <div className="textBox">
            <span>{t('didNotRegisterYet')}</span>
            <span className="link" onClick={props.gotoRegister}>
              {t('register')}
            </span>
          </div>
        </>
      )}
      {(step === 2 || step === 3) && (
        <div className="textBox">
          <span className="link" onClick={() => setStep(1)}>
            {t('returnToLogin')}
          </span>
        </div>
      )}
    </div>
  );
};

const Register = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const query = new URLSearchParams(useLocation().search);
  const registerState = useSelector(state => state.registration.registerState);
  const resendSMSState = useSelector(state => state.registration.resendSMSState);
  const error = useSelector(state => state.registration.error);
  const [messageContent, setMessageContent] = useState('');
  const [hideMessage, setHideMessage] = useState(true);
  const [step, setStep] = useState(1);
  const usernameRef = useRef('');
  const [counter, setCounter] = useState(120);
  const localLanguage = localStorage.getItem('language');

  useEffect(() => {
    return () => {
      dispatch(userActions.resetState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (registerState === 'succeeded') {
      if (step === 1 && regexMobile.test(usernameRef.current)) {
        setMessageContent(t('registerMsgSMS'));
        setStep(2);
      } else {
        if (regexMobile.test(usernameRef.current)) {
          setMessageContent(t('registerMsgActive'));
        } else {
          setMessageContent(t('registerMsgEmail'));
        }
        setStep(3);
      }
    } else if (registerState === 'failed' && error) {
      switch (error.status) {
        case 400:
          setMessageContent(t('confirmationError400'));
          break;
        case 409:
          if (step === 1) setMessageContent(t('registerError409'));
          break;
        case 490:
          setMessageContent(t('registerError49X'));
          break;
        case 491:
          setMessageContent(t('registerError49X'));
          break;
        case 492:
          setMessageContent(t('registerError49X'));
          break;
        case 500:
          setMessageContent(t('error500'));
          break;
        default:
          setMessageContent(t('registerError'));
          break;
      }
    }
    setHideMessage(!(registerState === 'failed' || registerState === 'succeeded'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerState]);

  useEffect(() => {
    step === 2 && counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
  }, [counter, step]);

  useEffect(() => {
    if (resendSMSState === 'success') {
      setCounter(120);
    }
  }, [resendSMSState]);

  const RegisterSchema = Yup.object().shape({
    username: Yup.string()
      .required(t('yupUsernameReq'))
      .matches(
        new RegExp(localLanguage === 'rtl/fa-IR' ? regexMobile.source : regexEmail.source),
        t('yupUsernameRegex'),
      ),
    password: Yup.string()
      .min(6, t('yupPasswordValidate'))
      .required(t('yupPasswordReq')),
    confirmPassword: Yup.string()
      .required(t('yupConfirmPassReq'))
      .oneOf([Yup.ref('password'), null], t('yupConfirmPassValidate')),
    referralCode: Yup.string().nullable(),
  });

  const ConfirmCodeSchema = Yup.object().shape({
    code: Yup.number()
      .min(6, t('yupConfirmCodeValidate'))
      .required(t('yupConfirmCodeReq')),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      confirmPassword: '',
      referralCode: query.get('ref'),
    },
    validationSchema: RegisterSchema,
    onSubmit: values => {
      if (values.username && values.password) {
        if (regexMobile.test(values.username)) usernameRef.current = values.username;
        dispatch(
          userActions.register({
            username: values.username,
            password: values.password,
            referralCode: values.referralCode,
          }),
        );
      }
    },
  });

  const confirmFormik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: ConfirmCodeSchema,
    onSubmit: values => {
      dispatch(userActions.confirmMobile({ username: usernameRef.current, code: values.code }));
    },
  });

  return (
    <div>
      <Grid centered verticalAlign="middle">
        <Grid.Column>
          <Message
            className="enteryFormMessage"
            hidden={hideMessage}
            floating
            error={registerState === 'failed'}
            positive={registerState === 'succeeded'}
            header={error ? t('registerErrorHeaderFail') : t('registerErrorHeaderSuccess')}
            content={messageContent}
          />
          {step === 1 && (
            <>
              <Header as="h2" textAlign="center" content={t('registerHeader')} />
              <Form size="large" className="VForm">
                <Segment basic>
                  <Form.Input
                    error={formik.touched.username ? formik.errors.username : null}
                    disabled={registerState === 'registering'}
                    required
                    fluid
                    icon="user"
                    placeholder={t('username')}
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                  />
                  <Form.Input
                    error={formik.touched.password ? formik.errors.password : null}
                    disabled={registerState === 'registering'}
                    required
                    fluid
                    icon="lock"
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                  />

                  <Form.Input
                    error={formik.touched.confirmPassword ? formik.errors.confirmPassword : null}
                    disabled={registerState === 'registering'}
                    required
                    fluid
                    icon="lock"
                    placeholder={t('confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                  />

                  <Form.Input
                    error={formik.touched.referralCode ? formik.errors.referralCode : null}
                    disabled={registerState === 'registering'}
                    fluid
                    icon="tag"
                    placeholder={`${t('referralCode')} (${t('optional')})`}
                    type="text"
                    name="referralCode"
                    value={formik.values.referralCode}
                    onChange={formik.handleChange}
                  />

                  <Button
                    basic
                    className=" vButton colorPrimary"
                    fluid
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={registerState === 'registering'}
                    onClick={formik.handleSubmit}
                    content={t('register')}
                  />
                  {/* <Button basic color="blue" fluid size="large">
                <Icon name="google" color="red" /> ورود با حساب کاربری گوگل
              </Button> */}
                </Segment>
              </Form>
            </>
          )}
          {step === 2 && (
            <>
              <Header as="h2" textAlign="center" content={t('confirmMobileHeader')} />
              <Form size="large" className="VForm">
                <Segment basic>
                  <Form.Input
                    error={confirmFormik.touched.code ? confirmFormik.errors.code : null}
                    disabled={registerState === 'registering'}
                    required
                    fluid
                    icon="tag"
                    placeholder={t('confirmCode')}
                    type="text"
                    name="code"
                    value={confirmFormik.values.code}
                    onChange={confirmFormik.handleChange}
                  />
                  <div>
                    {counter > 0 ? (
                      `${Math.floor(counter / 60)
                        .toString()
                        .padStart(2, '0')}:${(counter % 60).toString().padStart(2, '0')}`
                    ) : (
                        <Segment
                          as="span"
                          basic
                          className="resend-link"
                          onClick={() =>
                            dispatch(
                              userActions.resendConfirmationSMS({ username: usernameRef.current }),
                            )
                          }
                        >
                          <Loader active={resendSMSState === 'resending'} />
                          {t('resend')}
                        </Segment>
                      )}
                  </div>

                  <Button
                    basic
                    className=" vButton colorPrimary"
                    fluid
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={registerState === 'registering'}
                    onClick={confirmFormik.handleSubmit}
                    content={t('send')}
                  />
                </Segment>
              </Form>
            </>
          )}
        </Grid.Column>
      </Grid>
      {step === 1 && (
        <div className="textBox">
          <span>{t('alreadyRegistered')}</span>
          <span
            className="link"
            onClick={() => {
              setHideMessage(true);
              setMessageContent('');
              props.gotoLogin();
            }}
          >
            {t('login')}
          </span>
        </div>
      )}
      {step === 2 && (
        <div className="textBox">
          <span
            className="link"
            onClick={() => {
              setHideMessage(true);
              setMessageContent('');
              setStep(1);
            }}
          >
            {t('changeMobile')}
          </span>
        </div>
      )}
      {step === 3 && (
        <div className="textBox">
          <span
            className="link"
            onClick={() => {
              props.gotoLogin();
            }}
          >
            {t('gotoLogin')}
          </span>
        </div>
      )}
    </div>
  );
};

Login.propTypes = {
  gotoRegister: PropTypes.func,
};

Register.propTypes = {
  gotoLogin: PropTypes.func,
};

EntryForm.propTypes = {
  form: PropTypes.string,
};

export default EntryForm;
